import {
  AccountNavBarSettings,
  LegacyAccountNavBarSettings,
} from "#src/batteries-included-components/Layouts/Authentication/AccountNavBar";
import {
  AuthenticatedContextActiveAuthVersionType,
  AuthenticatedContextAuthVersionType,
} from "#src/contexts/AuthenticatedContext";
import {
  useAuthenticatedContext,
  useGetHolisticAuthorizationEvaluators,
} from "#src/contexts/AuthenticatedContext.helpers";
import { type SideNavigationTabType } from "@validereinc/common-components";
import type { AuthQueryType } from "@validereinc/domain-controllers";
import { useMemo } from "react";
import PageConfigs from "../../Routers/PageConfigs";
import {
  LegacyOpsHubFeatures,
  useSideNavTabsWithMapping,
} from "./SideNavigation/SideNavigationConfiguration";

// TODO: note this was made during a temporary refactor as this is eventually intended to be fully thrown away in the routing engine refactor.
/**
 * Get all the localized side nav tabs that are available because they passed
 * feature flags and permissions checks. Use this list to generate the side nav
 * and top nav (unfortunate tech debt at time of writing), navigational
 * elements, for an authenticated session.
 * @returns a memoized list of all the available side nav tabs
 */
export const useGetSideNavTabsAvailable = ({
  onClickBindings,
}: {
  onClickBindings: Record<
    SideNavigationTabType["id"],
    Pick<SideNavigationTabType, "id" | "renderLink">
  >;
}): [SideNavigationTabType[], boolean] => {
  const {
    activeAuthVersion,
    v1: {
      user: legacyUser,
      featureFlags: legacyFeatureFlags,
      permissions: legacyPermissions,
    },
    v2: {
      featureFlags: featureFlagsGlobal,
      userInfo: { permissions },
      companyInfo: { featureFlags, checkIsFeatureEnabled },
    },
  } = useAuthenticatedContext();
  const { isFeatureEnabledWaterfall, hasPermissionsWaterfall, isUserAllowed } =
    useGetHolisticAuthorizationEvaluators();
  const [sideNavTabs, isSideNavTabsLoading] =
    useSideNavTabsWithMapping(onClickBindings);

  const getAssociatedPageConfig = (tab: SideNavigationTabType) =>
    PageConfigs[tab.id];

  const availableSideNavTabs = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return sideNavTabs.reduce<SideNavigationTabType[]>(
      (availableTabs, currentTab) => {
        const newCurrentTab: SideNavigationTabType = { ...currentTab };
        const hasNestedTabs = Boolean(newCurrentTab.nested?.length);

        // if the tab config has nested tabs...
        if (hasNestedTabs) {
          // run the evaluators for each nested tab and filter out tabs that are denied...
          newCurrentTab.nested = newCurrentTab.nested?.filter((nestedTab) => {
            const shouldBeInNewNav = isNewAccountNavigationEnabled(
              nestedTab.id,
              {
                activeAuthVersion,
                checkIsFeatureEnabled,
              }
            );

            if (shouldBeInNewNav) {
              return false;
            }

            const associatedPageConfig = getAssociatedPageConfig(nestedTab);

            if (!associatedPageConfig) {
              return false;
            }

            return (
              isFeatureEnabledWaterfall({
                featureFlagQuery: associatedPageConfig.get("featureFlagsQuery"),
                legacyFeatureFlagQuery: associatedPageConfig.get(
                  "legacyFeatureFlagsQuery"
                ),
                legacyNotFeatureFlagQuery: associatedPageConfig.get(
                  "legacyInverseFeatureFlagQuery"
                ),
                featureId: nestedTab.id,
              }) &&
              hasPermissionsWaterfall({
                permissionsQuery: associatedPageConfig.get("permissionsQuery"),
                legacyPermissionsQuery: associatedPageConfig.get(
                  "legacyPermissionsQuery"
                ),
                shouldCheckPermissions: associatedPageConfig.get(
                  "shouldCheckPermissions"
                ),
              }) &&
              isUserAllowed({
                requiresInternalUser: associatedPageConfig.get(
                  "requiresInternalUser"
                ),
              })
            );
          });

          // if all the nested tabs get denied, then we don't show this root tab either
          if (!newCurrentTab.nested?.length) {
            return availableTabs;
          } else {
            // otherwise we allow access since some combination of nested tabs are allowed
            availableTabs.push(newCurrentTab);
            return availableTabs;
          }
        }

        const associatedPageConfigForRootTab =
          getAssociatedPageConfig(newCurrentTab);

        // if the associated page config doesn't exist, we skip the root tab
        if (!associatedPageConfigForRootTab) {
          return availableTabs;
        }

        const shouldBeInNewNav = isNewAccountNavigationEnabled(
          newCurrentTab.id,
          {
            activeAuthVersion,
            checkIsFeatureEnabled,
          }
        );

        if (shouldBeInNewNav) {
          return availableTabs;
        }

        // run evaluators against the root tab config to determine access
        const isRootTabAvailable =
          isFeatureEnabledWaterfall({
            featureFlagQuery:
              associatedPageConfigForRootTab.get("featureFlagsQuery"),
            legacyFeatureFlagQuery: associatedPageConfigForRootTab.get(
              "legacyFeatureFlagsQuery"
            ),
            legacyNotFeatureFlagQuery: associatedPageConfigForRootTab.get(
              "legacyInverseFeatureFlagQuery"
            ),
            featureId: newCurrentTab.id,
          }) &&
          hasPermissionsWaterfall({
            permissionsQuery:
              associatedPageConfigForRootTab.get("permissionsQuery"),
            legacyPermissionsQuery: associatedPageConfigForRootTab.get(
              "legacyPermissionsQuery"
            ),
            shouldCheckPermissions: associatedPageConfigForRootTab.get(
              "shouldCheckPermissions"
            ),
          }) &&
          isUserAllowed({
            requiresInternalUser: associatedPageConfigForRootTab.get(
              "requiresInternalUser"
            ),
          });

        // if the root tab is denied, hide the root tab
        if (!isRootTabAvailable) {
          return availableTabs;
        }

        // otherwise, show the root tab
        availableTabs.push(newCurrentTab);

        return availableTabs;
      },
      []
    );
  }, [
    sideNavTabs,
    isFeatureEnabledWaterfall,
    hasPermissionsWaterfall,
    isUserAllowed,
    legacyUser,
    legacyFeatureFlags,
    legacyPermissions,
    featureFlagsGlobal,
    featureFlags,
    permissions,
  ]);

  return [availableSideNavTabs, isSideNavTabsLoading];
};

/**
 * Use to determine if certain side nav entires that also appear in the new
 * account nav bar should be hidden from the side nav. This is temporary while
 * the old auth system is still being used.
 * @param sideNavTabId the ID of the side nav config
 * @returns true if it should be hidden, false otherwise
 */
const isNewAccountNavigationEnabled = (
  sideNavTabId: string,
  {
    activeAuthVersion,
    checkIsFeatureEnabled,
  }: {
    activeAuthVersion: AuthenticatedContextActiveAuthVersionType;
    checkIsFeatureEnabled: (query: AuthQueryType) => boolean;
  }
) => {
  const isV1AuthOnly =
    activeAuthVersion !== "unknown" &&
    Array.isArray(activeAuthVersion) &&
    activeAuthVersion.length === 1 &&
    activeAuthVersion.includes("v1");
  const isDualAuth =
    activeAuthVersion !== "unknown" &&
    Array.isArray(activeAuthVersion) &&
    activeAuthVersion.length === 2 &&
    (["v1", "v2"] satisfies AuthenticatedContextAuthVersionType[]).every((v) =>
      activeAuthVersion.includes(v)
    );
  const isV2AuthOnly =
    activeAuthVersion !== "unknown" &&
    Array.isArray(activeAuthVersion) &&
    activeAuthVersion.length === 1 &&
    activeAuthVersion.includes("v2");

  // hide the home page when the user is v1 auth only or when they're dual-auth and the `ops:auth` flag is enabled
  if (
    (isV1AuthOnly || (isDualAuth && checkIsFeatureEnabled("ops:auth"))) &&
    sideNavTabId === "home"
  ) {
    return true;
  }

  // hide legacy ops-hub features when user is dual-auth and the `ops:auth` flag is not enabled OR when the user is v2 auth only
  if (
    ((isDualAuth && !checkIsFeatureEnabled("ops:auth")) || isV2AuthOnly) &&
    Object.values(LegacyOpsHubFeatures).includes(sideNavTabId as never)
  ) {
    return true;
  }

  // hide non legacy ops-hub features when user is dual-auth and the `ops:auth` flag is enabled OR when the user is v1 auth only
  if (
    ((isDualAuth && checkIsFeatureEnabled("ops:auth")) || isV1AuthOnly) &&
    !Object.values(LegacyOpsHubFeatures).includes(sideNavTabId as never) &&
    !Object.values(LegacyAccountNavBarSettings).includes(
      sideNavTabId as never
    ) &&
    !Object.values(AccountNavBarSettings).includes(sideNavTabId as never) &&
    sideNavTabId !== "dashboards"
  ) {
    return true;
  }

  // hide the v2 auth settings and v1 auth settings when the user is a dual-auth
  // user AND they don't have the ops:auth flag enabled or they're a v2 auth only user
  if (
    ((isDualAuth && !checkIsFeatureEnabled("ops:auth")) || isV2AuthOnly) &&
    (Object.values(AccountNavBarSettings).includes(sideNavTabId as never) ||
      Object.values(LegacyAccountNavBarSettings).includes(
        sideNavTabId as never
      ))
  ) {
    return true;
  }

  return false;
};

/**
 * Used to determine whether the account navbar should be displayed.
 * @returns true if the account navbar should be displayed, false otherwise
 */
export const useIsAccountNavBarEnabled = () => {
  const {
    activeAuthVersion,
    v2: {
      companyInfo: { checkIsFeatureEnabled },
    },
  } = useAuthenticatedContext();

  const isDualAuth =
    activeAuthVersion !== "unknown" &&
    Array.isArray(activeAuthVersion) &&
    activeAuthVersion.length === 2 &&
    (["v1", "v2"] satisfies AuthenticatedContextAuthVersionType[]).every((v) =>
      activeAuthVersion.includes(v)
    );
  const isV2AuthOnly =
    activeAuthVersion !== "unknown" &&
    Array.isArray(activeAuthVersion) &&
    activeAuthVersion.length === 1 &&
    activeAuthVersion.includes("v2");
  const isAccountNavBarEnabled =
    (isDualAuth && !checkIsFeatureEnabled("ops:auth")) ||
    isV2AuthOnly ||
    checkIsFeatureEnabled("ops:auth");

  return isAccountNavBarEnabled;
};
